import * as SelectPrimitive from "@radix-ui/react-select";
import type * as React from "react";

import {
    Select as _Select,
    SelectContent,
    SelectValue,
} from "@@/shared/shadcdn/components/ui/select";
import { useIsTranslatableEmpty } from "@@/translations/use-translate";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import type { IconName, TranslatableType } from "@towni/common";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import { CheckIcon, ChevronDownIcon } from "lucide-react";
import { isValidElement } from "react";
import type { Except } from "type-fest";
import { DynamicIcon } from "../icons/dynamic-icon";
import { Icon } from "../icons/icon";
import { Span } from "../text/span";
import { cn } from "../utils/cn";
import { Row } from "./row";

type SelectOption<Value> = {
    label: TranslatableType;
    icon?: IconDefinition | IconName | React.JSX.Element;
    value: Value;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isJSXElement(icon: any): icon is React.JSX.Element {
    return isValidElement(icon);
}

type Props<Value extends string> = Except<
    React.ComponentProps<typeof _Select>,
    "onValueChange"
> & {
    readonly options: SelectOption<Value>[];
    readonly placeholderIcon?: IconDefinition | IconName;
    readonly placeholder?: TranslatableType;
    readonly triggerClassName?: string;
    readonly contentClassName?: string;
    readonly itemClassName?: string;
    readonly children?: never;
    readonly size?: "sm" | "md" | "lg";
    readonly onValueChange?: (value: Value) => void;
};

/**
 * A customizable Select component that renders a dropdown menu with support for icons and custom placeholders.
 *
 * @remarks
 * This component wraps a base Select component and enhances it with support for displaying icons alongside options and placeholders.
 * It is designed to be flexible and easily integrated into forms or UI panels where a styled select/dropdown is needed.
 *
 * @example
 * ```tsx
 * import { Select, SelectOption } from "./select";
 * import { faCoffee } from "@fortawesome/free-solid-svg-icons";
 *
 * const options: SelectOption[] = [
 *   { label: "Coffee", value: "coffee", icon: faCoffee },
 *   { label: "Tea", value: "tea" },
 *   { label: "Juice", value: "juice" }
 * ];
 *
 * function Example() {
 *   const [value, setValue] = React.useState<string | undefined>();
 *   return (
 *     <Select
 *       options={options}
 *       value={value}
 *       onValueChange={setValue}
 *       placeholder="Select a drink"
 *       placeholderIcon={faCoffee}
 *     />
 *   );
 * }
 * ```
 *
 * @param props - The props for the Select component.
 * @param props.options - An array of option objects to display in the dropdown. Each option can include a `label`, `value`, and optional `icon`.
 * @param props.placeholder - The placeholder text to display when no option is selected.
 * @param props.placeholderIcon - An optional icon (string or icon object) to display alongside the placeholder text.
 * @param props.triggerClassName - Optional additional class names for the select trigger element.
 * @param props.contentClassName - Optional additional class names for the dropdown content.
 * @param props.itemClassName - Optional additional class names for each dropdown item.
 * @param props.children - This component does not accept children.
 * @param ...passOnProps - Any additional props are passed to the underlying select component.
 *
 * @returns A select dropdown component with customizable options, icons, and placeholder.
 */
const Select = <Value extends string>(props: Props<Value>) => {
    const isTranslatableEmpty = useIsTranslatableEmpty();
    const {
        options,
        onValueChange: propsOnValueChange,
        placeholder: placeholderText,
        placeholderIcon,
        size = "md",
        ...passOnProps
    } = props;
    const placeholder = (
        <Row crossAxis="center" className="space-x-2">
            {/* <div className="flex flex-row items-center space-x-2"> */}
            {placeholderIcon ? (
                typeof placeholderIcon === "string" ? (
                    <DynamicIcon icon={placeholderIcon} />
                ) : (
                    <Icon icon={placeholderIcon} />
                )
            ) : null}
            <Span text={placeholderText} />
        </Row>
    );

    const onValueChange = (value: string) => {
        propsOnValueChange?.(value as Value);
    };

    return (
        <_Select {...passOnProps} onValueChange={onValueChange}>
            <SelectTrigger
                size={size}
                className={cn(
                    "bg-text-input-50 bg-neutral-surface border-neutral-outline rounded-sm",
                    props.triggerClassName,
                )}>
                <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent
                className={cn(
                    "border-neutral-outline border-1",
                    props.contentClassName,
                )}>
                {options.map((option, index) => {
                    return (
                        <SelectItem
                            key={index}
                            size={size}
                            value={option.value}
                            className={props.itemClassName}>
                            <Row crossAxis="center" className="space-x-2.5">
                                {option.icon ? (
                                    typeof option.icon === "string" ? (
                                        <DynamicIcon
                                            icon={option.icon}
                                            css={{
                                                color: "inherit",
                                            }}
                                        />
                                    ) : isJSXElement(option.icon) ? (
                                        option.icon
                                    ) : (
                                        <Icon
                                            icon={option.icon}
                                            css={{
                                                color: "inherit",
                                            }}
                                        />
                                    )
                                ) : null}
                                {isTranslatableEmpty(option.label) ? null : (
                                    <Span text={option.label} />
                                )}
                            </Row>
                        </SelectItem>
                    );
                })}
            </SelectContent>
        </_Select>
    );
};

const selectTriggerVariants = cva("", {
    variants: {
        size: {
            sm: "h-8",
            md: "h-10 text-base [&_svg:not([class*='size-'])]:size-5",
            lg: "h-12 text-lg [&_svg:not([class*='size-'])]:size-6",
        },
    },
});

const SelectTrigger = ({
    className,
    size,
    children,
    ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> &
    VariantProps<typeof selectTriggerVariants>) => {
    return (
        <SelectPrimitive.Trigger
            data-slot="select-trigger"
            data-size={size}
            className={cn(
                "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-fit items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                selectTriggerVariants({ size }),
                className,
            )}
            {...props}>
            {children}
            <SelectPrimitive.Icon asChild>
                <ChevronDownIcon className="size-4 opacity-50" />
            </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
    );
};

const selectItemVariants = cva("", {
    variants: {
        size: {
            sm: "",
            md: "text-base [&_svg:not([class*='size-'])]:size-5",
            lg: "text-lg [&_svg:not([class*='size-'])]:size-6",
        },
    },
});

const SelectItem = ({
    className,
    children,
    size,
    ...props
}: React.ComponentProps<typeof SelectPrimitive.Item> &
    VariantProps<typeof selectItemVariants>) => {
    return (
        <SelectPrimitive.Item
            data-slot="select-item"
            className={cn(
                "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
                selectItemVariants({ size }),
                className,
            )}
            {...props}>
            <span className="absolute right-2 flex size-3.5 items-center justify-center">
                <SelectPrimitive.ItemIndicator>
                    <CheckIcon className="size-4" />
                </SelectPrimitive.ItemIndicator>
            </span>
            <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        </SelectPrimitive.Item>
    );
};

export { Select };
export type { SelectOption };
